/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import {
	Tab,
	Tabs,
	Typography,
	TextField,
	Button,
	IconButton,
	Divider,
	Alert,
	Tooltip,
	Box,
	Card,
	CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import "./Profile.css";
import PageTitle from "../../components/PageTitle/PageTitle";
import customHttp from "../../services/customHTTP";
import Datagrid from "../../components/Datagrid/Datagrid";
import Settings from "../Settings/Settings";
import EditUserForm from "../../components/EditUserForm/EditUserForm";
import ProfileForm from "../../components/ProfileForm/ProfileForm";
import PasswordForm from "../../components/PasswordForm/PasswordForm";
import NewUserForm from "../../components/NewUserForm/NewUserForm";
import Swal from "sweetalert2";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ManageDocuments from "../ManageDocuments/ManageDocuments";
import { useSite } from "../../context/siteContext";
import ManageSites from "../ManageSites/ManageSites";
import { useAuth } from "../../context/authContext";

function Profile() {
	// Retrieve and parse user details from session storage

	// State for tab navigation
	const [tab, setTab] = useState(0);

	const [currentUser, setCurrentUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [allUsers, setAllUsers] = useState([]);
	const [allCustomers, setAllCustomers] = useState([]);
	const [allSites, setAllSites] = useState([]);
	const [assignedSitesLoc, setAssignedSitesLoc] = useState([]);
	const [selectedUser, setSelectedUser] = useState(null); // State for selected user
	const [newUser, setNewUser] = useState({
		user_first_name: "",
		user_last_name: "",
		user_email: "",
		user_phone: "",
		user_role: "",
		user_status: "0", // Default status to "Pending"
	});
	const [createNewUser, setCreateNewUser] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [billingAddress, setBillingAddress] = useState("");
	const [shippingAddress, setShippingAddress] = useState("");
	const [selectedUsersRows, setSelectedUsersRows] = useState([]);
	const [allUsersKey, setAllUsersKey] = useState(Date.now());
	const { selectedSite, siteData } = useSite();
	const { user } = useAuth();
	// Handle tab changes

	useEffect(() => {
		// Fetch the user from sessionStorage when the component mounts
		setCurrentUser(user);
	}, []);

	useEffect(() => {
		const getAddress = async () => {
			try {
				const site = siteData.filter((i) => i.customer_site_number === selectedSite)[0];
				setShippingAddress(site.address_full_ln);
				const response = await customHttp.get(`/cus_sites/getcustomer/${site.cus_id}`);
				if (response.status === 200) {
					setBillingAddress(response.data.data.address_full_cus);
				}
			} catch (err) {
				console.error(err);
			}
		};

		if (selectedSite) getAddress();
	}, [selectedSite]);

	useEffect(() => {
		const fetchData = async (endpoint, setState) => {
			try {
				const response = await customHttp.get(endpoint);
				if (response.data.data.length > 0) {
					setState(response.data.data);
				} else {
					setError(`Failed to fetch data: No data found for ${endpoint}.`);
				}
			} catch (err) {
				setError(`Failed to fetch data from ${endpoint}: ${err.message}`);
			} finally {
				setLoading(false);
			}
		};

		if (currentUser && currentUser.user_role === "Admin") {
			setLoading(true);
			fetchData("/users", setAllUsers);
			fetchData("/cus_sites/getcustomers", setAllCustomers);
			fetchData("/cus_sites/getsites", setAllSites);
		} else {
			setLoading(false);
		}
	}, [currentUser]);

	useEffect(() => {
		refreshAllUsersKey();
	}, [allUsers]);

	const refreshAllUsersKey = () => setAllUsersKey(Date.now());

	const handleTabChange = (event, newValue) => {
		setTab(newValue);
	};

	const toggleEditing = () => {
		setIsEditing(!isEditing);
	};

	return (
		<>
			{!loading ? (
				<div className="profile-container">
					<PageTitle title="Profile" />
					<Tabs
						value={tab}
						onChange={handleTabChange}
						aria-label="profile page tabs"
						className="profile-tabs">
						<Tab label="My Info" />
						<Tab label="Update Password" />
						<Tab label="Address" />

						{/* <Tab label="Manage Theme" /> */}
					</Tabs>

					<div className="tab-content">
						{tab === 0 && (
							<div className="info-section">
								<Box
									display="flex"
									flexDirection="row"
									justifyContent="space-between"
									alignItems="center"
									width="100%">
									<Typography variant="h6" gutterBottom mb={2}>
										My Information
									</Typography>
									<Button
										variant="text"
										startIcon={<EditIcon />}
										onClick={toggleEditing}
										className="edit-button">
										Edit Profile
									</Button>
								</Box>
								<ProfileForm
									currentUser={currentUser}
									setCurrentUser={setCurrentUser}
									isEditing={isEditing}
									setIsEditing={setIsEditing}
								/>
							</div>
						)}
						{tab === 1 && (
							<div className="update-password-section">
								<Typography variant="h6" gutterBottom mb={2}>
									Update Password
								</Typography>
								<PasswordForm currentUser={currentUser} />
							</div>
						)}
						{tab === 2 && (
							<div className="address-section">
								<Typography variant="h6" gutterBottom mb={2}>
									Address
								</Typography>
								<Box
									display="flex"
									flexWrap="wrap"
									justifyContent="space-between"
									alignItems="flex-start"
									gap="20px"
									width="100%"
									padding="20px"
									sx={{}}>
									{/* Shipping Address */}
									<Box
										flex="1"
										minWidth="300px"
										padding="20px"
										sx={{
											backgroundColor: "#ffffff",
											borderRadius: "8px",
											boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
										}}>
										<Typography
											variant="h6"
											sx={{
												color: "var(--primary-main)",
												fontWeight: "bold",
												marginBottom: "15px",
											}}>
											Shipping Address
										</Typography>
										<Typography variant="body1" color="text.secondary" gutterBottom>
											{/* Replace with dynamic content */}
											{shippingAddress}
										</Typography>
									</Box>

									{/* Billing Address */}
									<Box
										flex="1"
										minWidth="300px"
										padding="20px"
										sx={{
											backgroundColor: "#ffffff",
											borderRadius: "8px",
											boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
										}}>
										<Typography
											variant="h6"
											sx={{
												color: "var(--primary-main)",
												fontWeight: "bold",
												marginBottom: "15px",
											}}>
											Billing Address
										</Typography>
										<Typography variant="body1" color="text.secondary" gutterBottom>
											{/* Replace with dynamic content */}
											{billingAddress}
										</Typography>
									</Box>
								</Box>
							</div>
						)}

						{tab === 6 && (
							<div className="manage-theme-section">
								<Typography variant="h6" gutterBottom>
									Manage Theme
								</Typography>
								<Typography variant="body1">Change the Theme colors.</Typography>
								<div>
									<Settings />
								</div>
							</div>
						)}
					</div>
				</div>
			) : (
				<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
					<CircularProgress />
				</Box>
			)}
		</>
	);
}

export default Profile;
